'use client';

import { Dialog, PulseButton } from 'src/components/PulseUI';
import { StepIndicator } from 'src/components';

interface MultiStepDialogProps {
  open: boolean;
  title: string;
  children: React.ReactNode;
  onSubmit: () => void;
  onClose: () => void;
  steps: string[];
  activeStep: number;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  onReset?: () => void;
  loading?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const MultiStepDialog = ({
  loading = false,
  disabled = false,
  size = 'xl',
  ...props
}: MultiStepDialogProps) => {
  function isLastStep() {
    if (props.steps.length <= 1) return true;
    const totalSteps = props.steps.length - 1;
    return props.activeStep === totalSteps;
  }

  function handleNext() {
    props.setActiveStep((prev) => {
      if (!isLastStep()) {
        return prev + 1;
      }
      return prev;
    });
  }

  function handleBack() {
    props.setActiveStep((prevActiveStep) => prevActiveStep - 1);
    // reset react-form if first step
    if (props.activeStep === 1 && props.onReset) props.onReset();
  }

  function handleStep(step: number) {
    props.setActiveStep(step);
  }

  return (
    <Dialog
      isOpen={props.open}
      onClose={props.onClose}
      size={size}
      className="!h-auto !max-h-[90vh]"
    >
      <Dialog.Header>
        <h2 className="text-lg font-semibold">{props.title}</h2>
      </Dialog.Header>

      <Dialog.Content>
        <div className="px-4 py-3">
          <StepIndicator
            steps={props.steps}
            activeStep={props.activeStep}
            completed={isLastStep()}
            handleStep={handleStep}
          />
        </div>
        <div className="px-4 py-2">{props.children}</div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="w-full flex justify-between gap-4">
          <PulseButton
            variant="outline"
            color="neutral"
            disabled={props.activeStep === 0}
            onClick={handleBack}
          >
            Back
          </PulseButton>
          <div className="flex items-center justify-end gap-2">
            <PulseButton
              variant="outline"
              color="neutral"
              onClick={props.onClose}
            >
              Cancel
            </PulseButton>
            {isLastStep() ? (
              <PulseButton
                variant="solid"
                color="success"
                onClick={props.onSubmit}
                disabled={disabled}
                loading={loading}
              >
                Save
              </PulseButton>
            ) : (
              <PulseButton
                variant="solid"
                color="primary"
                onClick={handleNext}
                disabled={disabled}
              >
                Next
              </PulseButton>
            )}
          </div>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default MultiStepDialog;
